import { useNavigate, useParams } from "react-router-dom";
import { teachersApi } from "../../services/teachers/teachersApi";
import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import Error from "../../components/common/Error";
import { FormikProvider, useFormik } from "formik";
import { TeacherRequest } from "../../models/dtos";
import { Card } from "primereact/card";
import { Avatar } from "primereact/avatar";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import { Button } from "primereact/button";
import CopyToClipboard from "react-copy-to-clipboard";
import { Message } from "primereact/message";
import { departmentsApi } from "../../services/nomenclatures/departmentsApi";
import { academicPositionsApi } from "../../services/nomenclatures/academicPositionsApi";
import { academicRanksApi } from "../../services/nomenclatures/academicRanksApi";
import { teacherStatusesApi } from "../../services/nomenclatures/teacherStatusesApi";
import TeacherForm from "./TeacherForm";
import teachersValidationSchema from "../students/helpers/teachersValidationSchema";
import { Steps } from "primereact/steps";
import TeacherProtocols from "./TeacherProtocols";
import TeacherSchedule from "./TeacherSchedule";

const academicPositionsMapping: { [key: string]: string } = {
  "Професор / ст.н.с І степен": "проф.",
  "Доцент / ст.н.с ІІ степен": "доц.",
  Асистент: "ас.",
  "Главен асистент": "гл. ас.",
  Доцент: "доц.",
  Преподавател: "преп.",
  Професор: "проф.",
  "Асистент / н.с": "ас. н.с.",
};

const academicRankingsMapping: { [key: string]: string } = {
  доктор: "д-р",
  "член - кореспондент": "чл. к-т",
  "почетен доктор": "поч. д-р",
  "доктор на науките": "д.н.",
  академик: "акад.",
};

const TeacherProfile = () => {
  const toast = useRef<Toast>(null);
  const { teacherId } = useParams();
  const navigate = useNavigate();

  const { data: teacher, isLoading, error } = teachersApi.useGetTeacherQuery(parseInt(teacherId!));

  const [updateTeacherProfile, { isLoading: isUpdateTeacherLoading, isError: isUpdateTeacherError }] =
    teachersApi.useCreateOrUpdateTeacherMutation();
  const [updateTeacherPicture, { isLoading: isUpdatingTeacherPicture }] = teachersApi.useUpdatePictureMutation();

  const { data: departments } = departmentsApi.useGetDepartmentsQuery();
  const { data: academicPositions } = academicPositionsApi.useGetAcademicPositionsQuery();
  const { data: academicRanks } = academicRanksApi.useGetAcademicRanksQuery();
  const { data: teacherStatuses } = teacherStatusesApi.useGetTeacherStatusesQuery();

  const [activeStep, setActiveStep] = useState(0);

  const steps = [{ label: "Основни данни" }, { label: "Протоколи" }, { label: "Разписи" }];

  const handleSelectStep = (e: any) => {
    window.location.hash = e.index;
    setActiveStep(e.index);
  };

  useEffect(() => {
    const stepIndex = parseInt(window.location.hash.replace("#", ""));
    if (!Number.isNaN(stepIndex)) {
      setActiveStep(stepIndex);
    }
  }, []);

  const onUpload = (event: FileUploadHandlerEvent) => {
    if (teacherId) {
      updateTeacherPicture({ teacherId: parseInt(teacherId), picture: event.files[0] }).then((response) => {
        if ("data" in response && response?.data) {
          toast.current!.show({ severity: "success", summary: "Успешно", detail: "Снимката е качена." });
        } else {
          toast.current!.show({
            severity: "error",
            summary: "Грешка",
            detail: "Снимката не е качена, моля проверете файла.",
          });
        }
      });
    }
  };

  const formik = useFormik({
    initialValues: {
      ...teacher?.data,
      employmentStartDate: teacher?.data?.employmentStartDate ? new Date(teacher?.data?.employmentStartDate) : null,
      employmentEndDate: teacher?.data?.employmentEndDate ? new Date(teacher?.data?.employmentEndDate) : null,
    } as Partial<TeacherRequest>,
    validationSchema: teachersValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (!values.teacherId) {
        values.teacherId = 0;
      }
      updateTeacherProfile(values)
        .unwrap()
        .then((response) => {
          if (response && "data" in response) {
            toast.current!.show({
              severity: "success",
              summary: "Успешно",
              detail:
                teacherId && parseInt(teacherId) !== 0 ? "Преподавателят е редактиран." : "Преподавателят е добавен.",
            });
            navigate(`/teachers/${response?.data}`);
          }
        })
        .catch(() => {
          toast.current!.show({
            severity: "error",
            summary: "Грешка",
            detail: "Възникна грешка при запазването на преподавателя.",
          });
        });
    },
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "s") {
        event.preventDefault();
        formik.handleSubmit();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [formik]);

  const saveButton = (
    <div className="flex flex-column align-items-end pl-4" style={{ flexBasis: "20%" }}>
      <Button
        label="Запази"
        className="text-xl font-bold py-3"
        size="large"
        icon="pi pi-save"
        badge="Ctrl + S"
        onClick={() => formik.handleSubmit()}
        disabled={isUpdateTeacherLoading}
      />
    </div>
  );

  const teacherAcademicPosition: string = teacher?.data?.academicPositionName
    ? academicPositionsMapping[teacher?.data?.academicPositionName]
    : "";

  const teacherRankingPosition: string = teacher?.data?.academicRankName
    ? academicRankingsMapping[teacher?.data?.academicRankName]
    : "";

  const teacherNav = (
    <div className="mt-4 flex flex-row justify-content-between">
      <div className="flex-grow-1">
        <Steps
          model={steps}
          activeIndex={activeStep}
          onSelect={handleSelectStep}
          readOnly={false}
          pt={{
            label: {
              className: "font-bold hover:text-primary",
            },
          }}
        />
      </div>
      {saveButton}
    </div>
  );

  if (isLoading) return <Skeleton className="w-full h-2rem" />;
  if (error) return <Error error={error} />;

  return (
    <div>
      <FormikProvider value={formik}>
        {!teacher?.data?.teacherId && <h1 className="font-light text-center">Добавяне на нов преподавател</h1>}

        {teacherNav}

        {isUpdateTeacherError && (
          <Message severity="error" text="Възникна грешка при запазването на данните." className="w-full my-4" />
        )}

        {formik.errors && Object.keys(formik.errors)?.length > 0 && (
          <Message severity="error" text="Моля попълнете всички задължителни полета." className="w-full my-4" />
        )}

        <div>
          {teacher?.data?.teacherId && (
            <Card className="w-full mt-4">
              <div className="flex flex-row">
                {isUpdatingTeacherPicture && <Skeleton className="w-8rem h-12rem mr-4" />}
                {!isUpdatingTeacherPicture && (
                  <div className="text-center">
                    <Avatar
                      image={`data:image/jpeg;base64,${teacher?.data?.picture}`}
                      style={{ width: "160px", height: "200px" }}
                      shape="square"
                    ></Avatar>
                    <FileUpload
                      mode="basic"
                      name="picture"
                      accept="image/*"
                      maxFileSize={5000000}
                      customUpload
                      auto
                      uploadHandler={onUpload}
                      chooseLabel="Снимка"
                      chooseOptions={{
                        className: "p-button-outlined p-button-secondary",
                      }}
                    />
                  </div>
                )}
                <div className="flex-grow-1 text-left pl-4">
                  <h2 className="text-3xl font-bold">
                    <div>
                      {teacherAcademicPosition} {teacherRankingPosition} {teacher?.data?.firstName}{" "}
                      {teacher?.data?.middleName} {teacher?.data?.lastName}
                    </div>
                    <div className="font-normal">
                      {teacher?.data?.firstNameEn} {teacher?.data?.middleNameEn} {teacher?.data?.lastNameEn}
                    </div>
                    <div className="font-normal flex flex-row gap-2 align-items-center"></div>
                    {teacher?.data?.email && (
                      <div className="font-normal flex flex-row gap-2 align-items-center">
                        <span className="pi pi-envelope" title="Студентски и-мейл"></span>
                        {teacher?.data?.email}
                        <CopyToClipboard
                          text={teacher?.data?.email!}
                          onCopy={() =>
                            toast.current!.show({
                              severity: "info",
                              summary: "Копирано",
                              detail: "Имейлът е копиран в клипборда.",
                            })
                          }
                        >
                          <Button
                            icon="pi pi-copy"
                            className="p-button-rounded p-button-secondary p-button-text"
                            tooltip="Копиране на преподавателски и-мейл в клипборда."
                            tooltipOptions={{ position: "bottom" }}
                          />
                        </CopyToClipboard>
                      </div>
                    )}
                  </h2>
                </div>

                <div className="flex-auto text-left pl-4 pt-4">
                  <h3>
                    <div className="text-sm">Факултет:</div> {teacher?.data?.facultyName}
                  </h3>
                  <h3>
                    <div className="text-sm">Катедра:</div> {teacher?.data?.departmentName}
                  </h3>
                </div>
              </div>
            </Card>
          )}

          {activeStep === 0 && (
            <TeacherForm
              formik={formik}
              departments={departments}
              academicPositions={academicPositions}
              academicRanks={academicRanks}
              teacherStatuses={teacherStatuses}
            />
          )}
          {activeStep === 1 && <TeacherProtocols teacherId={teacher?.data?.teacherId} />}
          {activeStep === 2 && <TeacherSchedule teacherId={teacher?.data?.teacherId} />}
        </div>

        {teacherNav}
        <Toast ref={toast} />
      </FormikProvider>
    </div>
  );
};

export default TeacherProfile;
